// FROM https://dev.to/n8tb1t/tracking-scroll-position-with-react-hooks-3bbj
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

interface Position {
  x: number;
  y: number;
}

interface ScrollPosition {
  prevPos: Position;
  currPos: Position;
}

type ScrollEffect = (scrollPosition: ScrollPosition) => void;
type ElementSelector = { current?: HTMLElement };

interface UseScrollPositionProps {
  effect: ScrollEffect;
  deps: unknown[];
  element?: ElementSelector;
  useWindow?: boolean;
  wait?: number;
}

const isBrowser = typeof window !== 'undefined';

function getScrollPosition({
  element,
  useWindow,
}: {
  element?: ElementSelector;
  useWindow: boolean;
}): { x: number; y: number } {
  if (!isBrowser) return { x: 0, y: 0 };

  const target = element ? element.current : document.body;
  const position = target.getBoundingClientRect();

  return useWindow
    ? { x: window.scrollX, y: window.scrollY }
    : { x: position.left, y: position.top };
}

function useScrollPosition({ effect, deps, element, useWindow, wait }: UseScrollPositionProps) {
  const position = useRef(getScrollPosition({ useWindow }));
  const throttleTimeoutRef = useRef(null);

  const callBack = () => {
    const currPos = getScrollPosition({ element, useWindow });
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeoutRef.current = null;
  };

  useIsomorphicLayoutEffect(() => {
    const handleScroll = () => {
      if (wait) {
        if (throttleTimeoutRef.current === null) {
          throttleTimeoutRef.current = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default useScrollPosition;
